import { outbox, type OutboxItem } from '../lib/outbox';
import type { OutboxView } from '../lib/useOutbox';
import { WifiOff } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

/**
 * 同步状态与冲突提示。
 *
 * ★ 冲突不能静默丢弃 —— 司机手上可能还有货。
 *   必须明确告诉他发生了什么、以及下一步做什么。
 * 见 docs/05-driver-app.md §05.6
 */
export function SyncBanner({ view, online }: { view: OutboxView; online: boolean }) {
  const { pending, conflicts } = view;

  /*
   * ★ 只剩两种情况会说话:**冲突**,和**离线**。
   *
   *   以前还有两条:「还在发送:送达、送达、送达…」和
   *   「N 项操作反复失败」。都去掉了,用户原话:
   *   「什么操作没有完成的提示不要显示给司机了吧,后台自己运行就行,
   *   然后数据保持幂等性就行」。
   *
   *   那两条的问题不只是吵。司机在路上,而这两条**他做不了任何事**:
   *   网不好他改变不了,「反复失败」的技术原因他也看不懂。
   *   更糟的是它们经常在说假话 —— 一条发失败的取货,他早就又点了一次、
   *   货也送到了,而队列里那条旧的还在重试。冲他喊「操作未生效」,
   *   除了让他慌没有任何用处。
   *
   *   现在那一类由服务端消化:同一个人迟到的重试,只要状态已经走过去了,
   *   就按成功处理、内部什么都不做(见 order-state.service 的 alreadyPast)。
   *   真的永远发不出去的,司机端上报到订单时间轴之后安静丢掉,调度看得见。
   *
   * ★ **冲突那张卡留着**,这是刻意的:订单被取消、被改派给别人的时候,
   *   货可能还在他手上。那件事没有别的途径能告诉他。
   */
  if (conflicts.length > 0) {
    return <ConflictCard item={conflicts[0]!} remaining={conflicts.length - 1} />;
  }

  if (!online) {
    return (
      <div className="sticky top-0 z-40 bg-amber-400 text-amber-950 px-4 py-2 text-center font-medium">
        <InlineIcon icon={WifiOff} /> 离线模式
        {pending.length > 0 && ` · ${pending.length} 项操作待同步`}
      </div>
    );
  }

  return null;
}

/** 动作的人话名字 —— 横幅和冲突卡共用一份,分两处写迟早对不上 */
function actionLabel(action: string): string {
  return (
    (
      {
        ACCEPT: '接单',
        PICKUP: '取货',
        DELIVER: '送达',
        FAIL: '标记未送达',
        GRAB: '领取',
        START_TRANSIT: '开始配送',
      } as Record<string, string>
    )[action] ?? action
  );
}

function ConflictCard({ item, remaining }: { item: OutboxItem; remaining: number }) {
  const label = actionLabel(item.action);

  return (
    <div className="mx-4 mt-3 rounded-xl bg-amber-50 dark:bg-amber-950/40 border-2
                    border-amber-400 dark:border-amber-700 px-4 py-3">
      <div className="font-semibold text-amber-900 dark:text-amber-200">
        ⚠ 有 {remaining + 1} 项操作未生效
      </div>
      <p className="mt-1.5 text-sm text-amber-800 dark:text-amber-300">
        你的「{label}」操作没能提交:{item.error?.message}
      </p>
      {/* 货可能还在司机手上 —— 必须给一个能立刻找到人的出口 */}
      {(item.action === 'PICKUP' || item.action === 'DELIVER') && (
        <p className="mt-1.5 text-sm text-amber-800 dark:text-amber-300">
          如果货还在你手上,请联系调度确认怎么处理。
        </p>
      )}
      <button
        onClick={() => void outbox.discard(item.id)}
        className="mt-2.5 w-full h-11 rounded-lg bg-amber-500 text-white font-medium"
      >
        我知道了
      </button>
    </div>
  );
}
